import { FolderActions, useGetFolders } from "@modules/slides-library/features/folders";
import type { TFolderWithChildren } from "@/shared/types";
import { IconChevronRight } from "@tabler/icons-react";
import { Fragment, useState } from "react";
import { useSlidesLibraryStoreShallow } from "../store";

const buildFolderPath = (
  folders: TFolderWithChildren[],
  targetId: string,
  path: TFolderWithChildren[] = [],
): TFolderWithChildren[] | null => {
  for (const folder of folders) {
    const current = [...path, folder];
    if (folder._id === targetId) return current;
    const found = buildFolderPath(folder.children, targetId, current);
    if (found) return found;
  }
  return null;
};

export const FolderBreadcrumbs = () => {
  const { folderId, type, setFolderId } = useSlidesLibraryStoreShallow((s) => ({
    folderId: s.folderId,
    type: s.type,
    setFolderId: s.setFolderId,
  }));
  const { folders } = useGetFolders();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isAtRoot = folderId === "user-slides" || folderId === "team-slides";
  if (isAtRoot) return null;

  const rootFolders = type === "team-slides" ? folders.teamFolders : folders.userFolders;
  const rootLabel = type === "team-slides" ? "Team slides" : "My slides";
  const path = buildFolderPath(rootFolders ?? [], folderId) ?? [];
  const currentFolder = path[path.length - 1];

  if (!currentFolder) return null;

  return (
    <nav
      aria-label="Folder breadcrumbs"
      className="tw-flex tw-h-14 tw-items-center tw-py-4 tw-px-6"
    >
      {/* Path labels — shrink freely */}
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1.5">
        <button
          type="button"
          aria-label={`Go to ${rootLabel}`}
          className="tw-shrink-0 tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-font-sans tw-text-[14px] tw-leading-5 tw-font-normal tw-text-blue-500 tw-outline-none hover:tw-underline"
          onClick={() => setFolderId(type)}
        >
          {rootLabel}
        </button>

        {path.map((folder, idx) => {
          const isLast = idx === path.length - 1;
          return (
            <Fragment key={folder._id}>
              <IconChevronRight className="tw-size-3.5 tw-shrink-0 tw-text-blue-500" stroke={1} aria-hidden="true" />
              {isLast ? (
                <>
                  <span
                    aria-current="page"
                    className="tw-truncate tw-font-sans tw-text-[14px] tw-leading-5 tw-font-medium tw-text-blue"
                  >
                    {folder.title}
                  </span>
                  <FolderActions
                    isOpen={isMenuOpen}
                    setIsOpen={setIsMenuOpen}
                    folder={folder}
                  />
                </>
              ) : (
                <button
                  type="button"
                  aria-label={`Go to ${folder.title}`}
                  className="tw-shrink-0 tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-font-sans tw-text-[14px] tw-leading-5 tw-font-normal tw-text-blue-500 tw-outline-none hover:tw-underline"
                  onClick={() => setFolderId(folder._id)}
                >
                  {folder.title}
                </button>
              )}
            </Fragment>
          );
        })}
      </div>
    </nav>
  );
};
